import React, { useCallback, useRef } from "react";

import { ColorResult } from "react-color";
import { TextState, Preview } from "@creatomate/preview";
import { classnames, PlainButton } from "@culturehq/components";

import { useDocumentEvent, useDoor } from "app/hooks";
import { containedBy, Trans } from "app/utils";

import TextEffectsOptions from "./TextEffectsOptions";
import styles from "./effects.module.scss";

interface TextEffectsButtonProps {
  className: string;
  closeOnOutsideClick: boolean;
  disabled: boolean;
  open: boolean;
  onClose: () => void;
  onToggle: () => void;
  selectedEffect: string;
}

const TextEffectsButton: React.FC<TextEffectsButtonProps> = ({
  className,
  children,
  disabled,
  onClose,
  open,
  onToggle,
  closeOnOutsideClick,
  selectedEffect
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useDocumentEvent("click", useCallback(
    event => {
      if (!containedBy(event.target, "QuickOptions") && containerRef.current && !containerRef.current.contains(event.target as Node) && closeOnOutsideClick) {
        onClose();
      }
    },
    [closeOnOutsideClick, onClose]
  ));

  const classList = classnames(
    "QuickOptions",
    "QuickOptions--Actions",
    className,
    styles.quick,
    styles[className],
    open && styles["quick-open"],
    disabled && styles["quick--disabled"]
  );

  return (
    <>
      <span className="chq-ffd--lb"><Trans>Font Effect</Trans></span>
      <div ref={containerRef} className={classList}>
        <div className="QuickOptions-Container">
          <PlainButton
            aria-label="Share"
            className={classnames(styles["quick--btn"])}
            disabled={disabled}
            onClick={onToggle}
          >
            <Trans>{selectedEffect}</Trans>
          </PlainButton>
        </div>
        {open && (
          <div className={classnames("QuickOptions__menu-wrapper", styles["quick--wrap"])}>
            <div className={classnames("QuickOptions__menu box-shadow", styles["quick--menu"])}>
              {children}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

interface TextEffectsProps {
  activeElement: TextState;
  className?: string;
  closeOnOutsideClick: boolean;
  color: ColorResult;
  disabled?: boolean;
  onSelectedEffect: (value: string) => void;
  preview: Preview;
  selectedEffect: string;
  onSubtitleEffectChange?: (properties: { property: string; value: string }[]) => Promise<void>;
}

const TextEffects: React.FC<TextEffectsProps> = ({
  activeElement,
  className = "",
  disabled = false,
  closeOnOutsideClick = false,
  color,
  onSelectedEffect,
  onSubtitleEffectChange,
  preview,
  selectedEffect
}) => {
  const { open, onClose, onToggle } = useDoor(false);

  return (
    <TextEffectsButton
      open={open}
      onClose={onClose}
      onToggle={onToggle}
      className={className}
      closeOnOutsideClick={closeOnOutsideClick}
      disabled={disabled}
      selectedEffect={selectedEffect}
    >
      <TextEffectsOptions
        activeElement={activeElement}
        color={color}
        onSelectedEffect={onSelectedEffect}
        onSubtitleEffectChange={onSubtitleEffectChange}
        onClose={onClose}
        preview={preview}
      />
    </TextEffectsButton>
  );
};

export default TextEffects;
